import { useTranslation } from 'react-i18next';
import {
  Accordion,
  Button,
  DescriptionList,
  IconButton,
  Modal,
  Stack,
  Text,
  Title
} from '@efast_public/fjd-component-library';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GovosApiRoutes,
  govosClient,
  invalidateServiceTemplatesCache
} from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import {
  Authentication,
  Payment,
  ServiceTemplate,
  ServiceTemplatesResponse,
  StartAppsResponse,
  TemplateServiceType,
  Zustellwege
} from '../api/govos.types';
import { FinalTemplateConfigurationModal } from '../modals/FinalTemplateConfigurationModal';
import { EditPaymentConfigModal } from '../modals/EditPaymentConfigModal';
import { EditZustellwegConfigModal } from '../modals/EditZustellwegConfigModal';
import { EditAuthenticationOptionsModal } from '../modals/EditAuthenticationOptionsModal';
import { EditSchriftformConfigModal } from '../modals/EditSchriftformConfigModal';
import { CreateServiceTemplateModal } from '../modals/CreateServiceTemplateModal';

export function TemplateConfigurationPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();

  const [selectedTemplate, setSelectedTemplate] = useState<{
    template?: ServiceTemplate;
    serviceType: TemplateServiceType;
  } | null>(null);

  const [createForServiceType, setCreateForServiceType] =
    useState<TemplateServiceType | null>(null);

  const [shouldShowCreateGesamtvorlage, setShouldShowCreateGesamtvorlage] =
    useState(false);

  const [
    shouldShowNoEinzelvorlagenWarning,
    setShouldShowNoEinzelvorlagenWarning
  ] = useState(false);

  const [templatePendingDelete, setTemplatePendingDelete] = useState<{
    template: ServiceTemplate;
    affectedGesamtvorlagen: ServiceTemplate[];
  } | null>(null);

  const { data: startApps } = useQuery<StartAppsResponse>({
    queryKey: [GovosApiRoutes.startApps()],
    queryFn: () =>
      govosClient
        .get<StartAppsResponse>(GovosApiRoutes.startApps())
        .then((r) => r.data)
  });

  // current workaround, to provide govOS a valid startAppId even if the endpoint does not one
  const tmpStartAppId = startApps?.userInfo.startApps.at(0)?.appiid ?? 0;

  const { data: templates } = useQuery<ServiceTemplatesResponse>({
    queryKey: [GovosApiRoutes.serviceTemplates(tmpStartAppId)],
    queryFn: () =>
      govosClient
        .get<ServiceTemplatesResponse>(
          GovosApiRoutes.serviceTemplates(tmpStartAppId)
        )
        .then((r) => r.data),
    enabled: !!startApps
  });

  const templatesByType = (type: ServiceTemplate['serviceType']) =>
    templates?.serviceTemplates.filter(
      (template) => template.serviceType === type
    ) ?? [];

  const handleCloseModal = () => {
    setSelectedTemplate(null);
  };

  const getAffectedGesamtvorlagen = (templateId: string): ServiceTemplate[] =>
    templatesByType('total').filter((total) => {
      if (!total.configuration) return false;
      try {
        const config = JSON.parse(total.configuration) as {
          authTemplateId?: string;
          deliveryTemplateId?: string;
          paymentTemplateId?: string;
          schriftformTemplateId?: string;
        };
        return (
          String(config.authTemplateId) === String(templateId) ||
          String(config.deliveryTemplateId) === String(templateId) ||
          String(config.paymentTemplateId) === String(templateId) ||
          String(config.schriftformTemplateId) === String(templateId)
        );
      } catch {
        return false;
      }
    });

  const { mutate: deleteTemplate } = useMutation({
    mutationFn: async (templateId: string) =>
      notifyUserFromRequest(
        () =>
          govosClient.delete(
            GovosApiRoutes.serviceTemplate(tmpStartAppId, templateId)
          ),
        {
          successMessage: t(
            'TemplateConfigurationPage.deleteTemplate.successMessage'
          ),
          errorMessage: t(
            'TemplateConfigurationPage.deleteTemplate.errorMessage'
          )
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
    }
  });

  const handleDeleteEinzelvorlage = (template: ServiceTemplate) => {
    const affected = getAffectedGesamtvorlagen(template.id);
    if (affected.length > 0) {
      setTemplatePendingDelete({ template, affectedGesamtvorlagen: affected });
    } else {
      deleteTemplate(template.id);
    }
  };

  const hasEinzelvorlagen =
    templatesByType('authentication').length > 0 ||
    templatesByType('delivery').length > 0 ||
    templatesByType('payment').length > 0 ||
    templatesByType('schriftform').length > 0;

  return (
    <div
      style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}
    >
      <Stack spacing="5xl">
        <Title level="3">{t('TemplateConfigurationPage.title')}</Title>

        <Stack spacing="xxl" orientation="vertical">
          <Stack spacing="s">
            <Title level="4">
              {t('TemplateConfigurationPage.einzelkonfigurationenSection')}
            </Title>

            {/* Authentifizierungsoptionen */}
            <div>
              <Accordion
                label={t(
                  'TemplateConfigurationPage.authentifizierungsoptionen.title'
                )}
                after={
                  <Button
                    variant="primary-text"
                    beforeIcon="add"
                    size="sm"
                    onClick={() => setCreateForServiceType('authentication')}
                  >
                    {t(
                      'TemplateConfigurationPage.authentifizierungsoptionen.addButton'
                    )}
                  </Button>
                }
              >
                <DescriptionList>
                  {templatesByType('authentication').map((template, index) => (
                    <React.Fragment key={template.id}>
                      {index > 0 && (
                        <hr className="fjd2-description-list_divider" />
                      )}
                      <DescriptionList.Item
                        title={
                          <div
                            style={{
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <p>{template.name}</p>
                          </div>
                        }
                        description={
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: '8px'
                            }}
                          >
                            <IconButton
                              variant="primary-text"
                              icon="edit"
                              size="sm"
                              label={t(
                                'TemplateConfigurationPage.actions.editAuthOptions'
                              )}
                              onClick={() =>
                                setSelectedTemplate({
                                  template,
                                  serviceType: 'authentication'
                                })
                              }
                            />
                            <IconButton
                              variant="primary-text"
                              icon="trashcan"
                              size="sm"
                              onClick={() =>
                                handleDeleteEinzelvorlage(template)
                              }
                              label={t(
                                'TemplateConfigurationPage.actions.delete'
                              )}
                            />
                          </div>
                        }
                      />
                    </React.Fragment>
                  ))}
                </DescriptionList>
              </Accordion>
            </div>

            {/* Schriftformerfordernis */}
            <div>
              <Accordion
                label={t('TemplateConfigurationPage.schriftform.title')}
                after={
                  <Button
                    variant="primary-text"
                    beforeIcon="add"
                    size="sm"
                    onClick={() => setCreateForServiceType('schriftform')}
                  >
                    {t('TemplateConfigurationPage.schriftform.addButton')}
                  </Button>
                }
              >
                <DescriptionList>
                  {templatesByType('schriftform').map((template, index) => (
                    <React.Fragment key={template.id}>
                      {index > 0 && (
                        <hr className="fjd2-description-list_divider" />
                      )}
                      <DescriptionList.Item
                        title={
                          <div
                            style={{
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <p>{template.name}</p>
                          </div>
                        }
                        description={
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: '8px'
                            }}
                          >
                            <IconButton
                              variant="primary-text"
                              icon="edit"
                              size="sm"
                              label={t(
                                'TemplateConfigurationPage.actions.editSchriftform'
                              )}
                              onClick={() =>
                                setSelectedTemplate({
                                  template,
                                  serviceType: 'schriftform'
                                })
                              }
                            />
                            <IconButton
                              variant="primary-text"
                              icon="trashcan"
                              size="sm"
                              onClick={() =>
                                handleDeleteEinzelvorlage(template)
                              }
                              label={t(
                                'TemplateConfigurationPage.actions.delete'
                              )}
                            />
                          </div>
                        }
                      />
                    </React.Fragment>
                  ))}
                </DescriptionList>
              </Accordion>
            </div>

            {/* Zustellwege and Bezahldienste */}
            {[
              { serviceType: 'delivery', titleKey: 'zustellwege' },
              { serviceType: 'payment', titleKey: 'bezahldienste' }
            ].map(({ serviceType, titleKey }) => (
              <div key={serviceType}>
                <Accordion
                  label={t(`TemplateConfigurationPage.${titleKey}.title`)}
                  after={
                    <Button
                      variant="primary-text"
                      beforeIcon="add"
                      size="sm"
                      onClick={() => {
                        setCreateForServiceType(
                          serviceType as TemplateServiceType
                        );
                      }}
                    >
                      {t(`TemplateConfigurationPage.${titleKey}.addButton`)}
                    </Button>
                  }
                >
                  <DescriptionList>
                    {templatesByType(serviceType as TemplateServiceType).map(
                      (template, index) => (
                        <React.Fragment key={template.id}>
                          {index > 0 && (
                            <hr className="fjd2-description-list_divider" />
                          )}
                          <DescriptionList.Item
                            title={
                              <div
                                style={{
                                  height: '40px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <p>{template.name}</p>
                              </div>
                            }
                            description={
                              <div
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: '8px'
                                }}
                              >
                                <IconButton
                                  variant="primary-text"
                                  icon="edit"
                                  size="sm"
                                  label={t(
                                    'TemplateConfigurationPage.actions.edit'
                                  )}
                                  onClick={async () => {
                                    // Preload data for payment and delivery templates
                                    if (serviceType === 'payment') {
                                      await queryClient.fetchQuery({
                                        queryKey: [
                                          GovosApiRoutes.paymentServices(
                                            tmpStartAppId
                                          )
                                        ]
                                      });
                                    }
                                    if (serviceType === 'delivery') {
                                      await queryClient.fetchQuery({
                                        queryKey: [
                                          GovosApiRoutes.deliveryServices(
                                            tmpStartAppId
                                          )
                                        ]
                                      });
                                    }
                                    setSelectedTemplate({
                                      template,
                                      serviceType:
                                        serviceType as TemplateServiceType
                                    });
                                  }}
                                />
                                <IconButton
                                  variant="primary-text"
                                  icon="trashcan"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteEinzelvorlage(template)
                                  }
                                  label={t(
                                    'TemplateConfigurationPage.actions.delete'
                                  )}
                                />
                              </div>
                            }
                          />
                        </React.Fragment>
                      )
                    )}
                  </DescriptionList>
                </Accordion>
              </div>
            ))}
          </Stack>

          <Stack spacing="s">
            <Title level="4">
              {t('TemplateConfigurationPage.gesamtkonfigurationenSection')}
            </Title>
            <div>
              <Accordion
                label={t('TemplateConfigurationPage.gesamtKonfiguration.title')}
                after={
                  <Button
                    variant="primary-text"
                    beforeIcon="add"
                    size="sm"
                    onClick={() => {
                      if (!hasEinzelvorlagen) {
                        setShouldShowNoEinzelvorlagenWarning(true);
                      } else {
                        setShouldShowCreateGesamtvorlage(true);
                      }
                    }}
                  >
                    {t(
                      'TemplateConfigurationPage.gesamtKonfiguration.addButton'
                    )}
                  </Button>
                }
              >
                <DescriptionList>
                  {templatesByType('total').map((template, index) => (
                    <React.Fragment key={template.id}>
                      {index > 0 && (
                        <hr className="fjd2-description-list_divider" />
                      )}
                      <DescriptionList.Item
                        title={
                          <div
                            style={{
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <p>{template.name}</p>
                          </div>
                        }
                        description={
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: '8px'
                            }}
                          >
                            <IconButton
                              variant="primary-text"
                              icon="edit"
                              size="sm"
                              label={t(
                                'TemplateConfigurationPage.actions.edit'
                              )}
                              onClick={async () => {
                                setSelectedTemplate({
                                  template,
                                  serviceType: 'total'
                                });
                              }}
                            />
                            <IconButton
                              variant="primary-text"
                              icon="trashcan"
                              size="sm"
                              onClick={() => deleteTemplate(template.id)}
                              label={t(
                                'TemplateConfigurationPage.actions.delete'
                              )}
                            />
                          </div>
                        }
                      />
                    </React.Fragment>
                  ))}
                </DescriptionList>
              </Accordion>
            </div>
          </Stack>
        </Stack>
      </Stack>

      {/* Warning: no Einzelvorlagen exist */}
      <Modal
        open={shouldShowNoEinzelvorlagenWarning}
        onClose={() => setShouldShowNoEinzelvorlagenWarning(false)}
      >
        <Modal.Header id="no-einzelvorlagen-warning-header">
          <Text size="xl" strong>
            {t('TemplateConfigurationPage.noEinzelvorlagenWarning.title')}
          </Text>
        </Modal.Header>
        <Modal.Content>
          <Text>
            {t('TemplateConfigurationPage.noEinzelvorlagenWarning.message')}
          </Text>
        </Modal.Content>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShouldShowNoEinzelvorlagenWarning(false)}
            style={{ width: '100%' }}
          >
            {t('TemplateConfigurationPage.noEinzelvorlagenWarning.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm delete Einzelvorlage used in Gesamtvorlagen */}
      {templatePendingDelete && (
        <Modal open onClose={() => setTemplatePendingDelete(null)}>
          <Modal.Header id="delete-einzelvorlage-confirm-header">
            <Text size="xl" strong>
              {t('TemplateConfigurationPage.deleteUsedTemplate.title')}
            </Text>
          </Modal.Header>
          <Modal.Content>
            <Stack spacing="m" orientation="vertical">
              <Text>
                {t('TemplateConfigurationPage.deleteUsedTemplate.message', {
                  name: templatePendingDelete.template.name
                })}
              </Text>
              <ul style={{ paddingLeft: '20px', margin: 0 }}>
                {templatePendingDelete.affectedGesamtvorlagen.map((g) => (
                  <li key={g.id}>
                    <Text>{g.name}</Text>
                  </li>
                ))}
              </ul>
              <Text>
                {t(
                  'TemplateConfigurationPage.deleteUsedTemplate.confirmQuestion'
                )}
              </Text>
            </Stack>
          </Modal.Content>
          <Modal.Footer>
            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <Button
                variant="secondary-outline"
                onClick={() => setTemplatePendingDelete(null)}
                style={{ flex: 1 }}
              >
                {t('TemplateConfigurationPage.deleteUsedTemplate.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  deleteTemplate(templatePendingDelete.template.id);
                  setTemplatePendingDelete(null);
                }}
                style={{ flex: 1 }}
              >
                {t('TemplateConfigurationPage.deleteUsedTemplate.confirm')}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {/* Create Einzelvorlage */}
      {createForServiceType && (
        <CreateServiceTemplateModal
          isOpen
          onClose={() => setCreateForServiceType(null)}
          serviceType={createForServiceType}
          startAppId={tmpStartAppId}
          templates={templatesByType(createForServiceType)}
          existingNames={templatesByType(createForServiceType).map(
            (template) => template.name
          )}
          onSuccess={(_, template) => {
            setCreateForServiceType(null);
            setSelectedTemplate({
              template,
              serviceType: createForServiceType
            });
          }}
        />
      )}

      {/* Create Gesamtvorlage */}
      {shouldShowCreateGesamtvorlage && (
        <FinalTemplateConfigurationModal
          isOpen
          onClose={() => setShouldShowCreateGesamtvorlage(false)}
          startAppId={tmpStartAppId}
          authenticationTemplates={templatesByType('authentication')}
          deliveryTemplates={templatesByType('delivery')}
          paymentTemplates={templatesByType('payment')}
          schriftformTemplates={templatesByType('schriftform')}
          totalTemplates={templatesByType('total')}
          existingNames={templatesByType('total').map(
            (template) => template.name
          )}
          onSuccess={() => setShouldShowCreateGesamtvorlage(false)}
        />
      )}

      {selectedTemplate?.serviceType === 'total' &&
        selectedTemplate.template && (
          <FinalTemplateConfigurationModal
            isOpen
            onClose={handleCloseModal}
            startAppId={tmpStartAppId}
            templateId={selectedTemplate.template.id}
            templateName={selectedTemplate.template.name}
            authenticationTemplates={templatesByType('authentication')}
            deliveryTemplates={templatesByType('delivery')}
            paymentTemplates={templatesByType('payment')}
            schriftformTemplates={templatesByType('schriftform')}
            totalTemplates={templatesByType('total')}
            existingNames={templatesByType('total').map(
              (template) => template.name
            )}
            onSuccess={handleCloseModal}
            configuration={
              selectedTemplate.template?.configuration
                ? JSON.parse(selectedTemplate.template?.configuration)
                : undefined
            }
          />
        )}
      {selectedTemplate?.serviceType === 'payment' && (
        <EditPaymentConfigModal
          isOpen
          onClose={handleCloseModal}
          startAppId={tmpStartAppId}
          templateId={selectedTemplate.template?.id}
          isTemplatePage
          templateName={selectedTemplate.template?.name}
          payment={
            selectedTemplate.template?.configuration
              ? (JSON.parse(
                  selectedTemplate.template?.configuration
                ) as Payment)
              : undefined
          }
          existingNames={templatesByType('payment').map(
            (template) => template.name
          )}
        />
      )}
      {selectedTemplate?.serviceType === 'delivery' && (
        <EditZustellwegConfigModal
          isOpen
          onClose={handleCloseModal}
          onSuccess={handleCloseModal}
          startAppId={tmpStartAppId}
          serviceIndex={selectedTemplate.template?.configuration ? 0 : -1}
          templateId={selectedTemplate.template?.id}
          isTemplatePage
          templateName={selectedTemplate.template?.name}
          zustellwege={
            selectedTemplate.template?.configuration
              ? ([
                  JSON.parse(selectedTemplate.template?.configuration)
                ] as Zustellwege[])
              : undefined
          }
          existingNames={templatesByType('delivery').map(
            (template) => template.name
          )}
        />
      )}
      {selectedTemplate?.serviceType === 'authentication' && (
        <EditAuthenticationOptionsModal
          isOpen
          onClose={handleCloseModal}
          startAppId={tmpStartAppId}
          templateId={selectedTemplate.template?.id}
          isTemplatePage
          templateName={selectedTemplate.template?.name}
          authentication={
            selectedTemplate.template?.configuration
              ? (JSON.parse(
                  selectedTemplate.template?.configuration
                ) as Authentication)
              : undefined
          }
          existingNames={templatesByType('authentication').map(
            (template) => template.name
          )}
        />
      )}
      {selectedTemplate?.serviceType === 'schriftform' && (
        <EditSchriftformConfigModal
          isOpen
          onClose={handleCloseModal}
          startAppId={tmpStartAppId}
          templateId={selectedTemplate.template?.id}
          isTemplatePage
          templateName={selectedTemplate.template?.name}
          authentication={
            selectedTemplate.template?.configuration
              ? (JSON.parse(
                  selectedTemplate.template?.configuration
                ) as Authentication)
              : undefined
          }
          existingNames={templatesByType('schriftform').map(
            (template) => template.name
          )}
        />
      )}
    </div>
  );
}
